import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Mock MainLayout component for demonstration
const MainLayout = ({ pageContent }) => <div>{pageContent}</div>;

export default function MlAdmin() {
    const [status, setStatus] = useState(null);
    const [stats, setStats] = useState({});
    const [healthData, setHealthData] = useState(null);
    const [trainingHistory, setTrainingHistory] = useState([]);
    const [performanceMetrics, setPerformanceMetrics] = useState([]);
    const [realtimeStats, setRealtimeStats] = useState({});
    const [automatedTrainingConfig, setAutomatedTrainingConfig] = useState({});
    const [cronStatus, setCronStatus] = useState({});
    const [trainingLimit, setTrainingLimit] = useState(1000);
    const [testMessage, setTestMessage] = useState('สวัสดีครับ มีปัญหาเรื่อง VST ไม่ทำงาน');
    const [testUser, setTestUser] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [trainingLog, setTrainingLog] = useState('');
    const [showLog, setShowLog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [alerts, setAlerts] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(new Date());

    // Colors for charts
    const COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'];

    // API helper function
    const apiRequest = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token');
        const url = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;

        const config = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        };

        const response = await fetch(url, config);
        return response.json();
    };

    // Fetch comprehensive system status
    const refreshStatus = useCallback(async () => {
        try {
            // Health check
            const healthData = await apiRequest('/suggestion?action=health');
            setHealthData(healthData);
            setStatus(healthData);

            // Statistics
            const statsData = await apiRequest('/suggestion?action=stats');
            if (statsData.success) {
                setStats(statsData.stats);

                // Update realtime stats for monitoring
                setRealtimeStats(prev => ({
                    ...prev,
                    timestamp: new Date().toLocaleTimeString(),
                    accuracy: statsData.stats.avg_confidence || 0,
                    usageRate: statsData.stats.usage_rate || 0,
                    suggestions: statsData.stats.suggestions_last_7_days || 0
                }));
            }

            // Training history (if available)
            try {
                const historyData = await apiRequest('/suggestion?action=training_history');
                if (historyData.success) {
                    setTrainingHistory(historyData.history || []);
                }
            } catch (e) {
                // Training history endpoint might not exist
            }

            // Automated training config
            try {
                const configData = await apiRequest('/suggestion?action=automation_config');
                if (configData.success) {
                    setAutomatedTrainingConfig(configData.config || {});
                }
            } catch (e) {
                // Config endpoint might not exist
            }

            // Generate alerts based on health data
            generateAlerts(healthData, statsData.stats);
            setLastUpdate(new Date());

        } catch (error) {
            setStatus({ success: false, error: error.message });
            setAlerts(prev => [...prev, {
                id: Date.now(),
                type: 'error',
                message: `Failed to refresh status: ${error.message}`,
                timestamp: new Date()
            }]);
        }
    }, []);

    // Generate alerts based on system health
    const generateAlerts = (health, stats) => {
        const newAlerts = [];
        const now = new Date();

        // ML Service alerts
        if (!health?.ml_service?.enabled) {
            newAlerts.push({
                id: `ml-offline-${now.getTime()}`,
                type: 'error',
                message: 'ML Service is offline',
                timestamp: now
            });
        }

        // Low usage rate alert
        if ((stats?.usage_rate || 0) < 30) {
            newAlerts.push({
                id: `low-usage-${now.getTime()}`,
                type: 'warning',
                message: `Low ML usage rate: ${stats?.usage_rate || 0}%`,
                timestamp: now
            });
        }

        // Low confidence alert
        if ((stats?.avg_confidence || 0) < 0.6) {
            newAlerts.push({
                id: `low-confidence-${now.getTime()}`,
                type: 'warning',
                message: `Low average confidence: ${Math.round((stats?.avg_confidence || 0) * 100)}%`,
                timestamp: now
            });
        }

        // Performance alerts
        if (health?.performance) {
            if (health.performance.suggestions_last_7_days < 10) {
                newAlerts.push({
                    id: `low-activity-${now.getTime()}`,
                    type: 'info',
                    message: 'Low system activity in the last 7 days',
                    timestamp: now
                });
            }
        }

        setAlerts(prev => {
            // Keep only recent alerts (last hour)
            const oneHourAgo = now.getTime() - (60 * 60 * 1000);
            const recentAlerts = prev.filter(alert => alert.timestamp.getTime() > oneHourAgo);
            return [...recentAlerts, ...newAlerts];
        });
    };

    // Start training with enhanced monitoring
    const startTraining = async () => {
        if (!trainingLimit || trainingLimit < 50) {
            alert('Please enter a valid number of conversations (minimum 50)');
            return;
        }

        setShowLog(true);
        setTrainingLog('🚀 Starting ML training...\n');
        setLoading(true);

        try {
            const data = await apiRequest('/suggestion', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'train',
                    training_limit: parseInt(trainingLimit)
                })
            });

            if (data.success) {
                setTrainingLog(prev => prev + '✅ Training initiated successfully!\n');
                setTrainingLog(prev => prev + `📊 Using ${trainingLimit} conversations\n`);
                setTrainingLog(prev => prev + '⏳ Training in progress... (this may take 5-15 minutes)\n');

                monitorTrainingProgress();
            } else {
                setTrainingLog(prev => prev + `❌ Training failed: ${data.error}\n`);
            }
        } catch (error) {
            setTrainingLog(prev => prev + `❌ Error: ${error.message}\n`);
        } finally {
            setLoading(false);
        }
    };

    // Enhanced training progress monitoring
    const monitorTrainingProgress = async () => {
        const maxChecks = 40;
        let checks = 0;

        const checkProgress = async () => {
            if (checks >= maxChecks) {
                setTrainingLog(prev => prev + '⏰ Training timeout. Check server logs.\n');
                return;
            }

            try {
                // Check if training is complete by testing the model
                const testData = await apiRequest('/suggestion', {
                    method: 'POST',
                    body: JSON.stringify({
                        message: 'สวัสดีครับ มีปัญหาเรื่อง VST ไม่ทำงาน',
                        user_line_id: 'test_user',
                        limit: 3
                    })
                });

                if (testData.success && testData.ml_suggestions_count > 0) {
                    setTrainingLog(prev => prev + '🎉 Training completed successfully!\n');
                    setTrainingLog(prev => prev + `📊 Model is working: ${testData.ml_suggestions_count} ML suggestions generated\n`);
                    setTrainingLog(prev => prev + '✅ Ready for production use!\n');

                    // Refresh all data after training
                    refreshStatus();
                    return;
                }

                checks++;
                setTrainingLog(prev => prev + `⏳ Still training... (${checks * 30} seconds elapsed)\n`);
                setTimeout(checkProgress, 30000);
            } catch (error) {
                checks++;
                setTrainingLog(prev => prev + `⚠️ Check ${checks} failed, retrying...\n`);
                setTimeout(checkProgress, 30000);
            }
        };

        setTimeout(checkProgress, 30000);
    };

    // Test model with comprehensive reporting
    const testModel = async () => {
        setShowLog(true);
        setTrainingLog('🧪 Running comprehensive model test...\n');
        setLoading(true);

        const testMessages = [
            'สวัสดีครับ มีปัญหาเรื่อง VST ไม่ทำงาน',
            'Cubase เสียงไม่ออกครับ ช่วยแก้ไขให้หน่อย',
            'FL Studio crash ตลอดเวลา มีวิธีแก้ไหมครับ',
            'ขอบคุณครับ แก้ไขปัญหาได้แล้ว เยี่ยมมาก',
            'ปลั๊กอิน VST ไม่โหลดใน DAW ครับ'
        ];

        let totalSuggestions = 0;
        let totalConfidence = 0;
        let mlSuggestions = 0;

        for (const [index, message] of testMessages.entries()) {
            try {
                const data = await apiRequest('/suggestion', {
                    method: 'POST',
                    body: JSON.stringify({
                        message: message,
                        user_line_id: `test_user_${index}`,
                        limit: 3
                    })
                });

                setTrainingLog(prev => prev + `\n📝 Test ${index + 1}: "${message.substring(0, 30)}..."\n`);
                if (data.success) {
                    const suggestions = data.suggestions || [];
                    const mlCount = data.ml_suggestions_count || 0;

                    totalSuggestions += suggestions.length;
                    mlSuggestions += mlCount;

                    suggestions.forEach(s => {
                        totalConfidence += s.confidence;
                    });

                    setTrainingLog(prev => prev + `   ✅ ${suggestions.length} suggestions (${mlCount} from ML)\n`);
                    setTrainingLog(prev => prev + `   📊 Avg confidence: ${Math.round((suggestions.reduce((sum, s) => sum + s.confidence, 0) / suggestions.length) * 100)}%\n`);
                } else {
                    setTrainingLog(prev => prev + `   ❌ Failed: ${data.error}\n`);
                }
            } catch (error) {
                setTrainingLog(prev => prev + `   ❌ Error: ${error.message}\n`);
            }
        }

        const avgConfidence = totalSuggestions > 0 ? totalConfidence / totalSuggestions : 0;
        const mlRatio = totalSuggestions > 0 ? mlSuggestions / totalSuggestions : 0;

        setTrainingLog(prev => prev + '\n🏁 Test Summary:\n');
        setTrainingLog(prev => prev + `   📊 Total suggestions: ${totalSuggestions}\n`);
        setTrainingLog(prev => prev + `   🤖 ML suggestions: ${mlSuggestions} (${Math.round(mlRatio * 100)}%)\n`);
        setTrainingLog(prev => prev + `   🎯 Average confidence: ${Math.round(avgConfidence * 100)}%\n`);
        setTrainingLog(prev => prev + `   ✅ System status: ${avgConfidence > 0.7 ? 'Excellent' : avgConfidence > 0.5 ? 'Good' : 'Needs improvement'}\n`);

        setLoading(false);
    };

    // Get suggestions with detailed analysis
    const getSuggestions = async () => {
        if (!testMessage.trim()) {
            alert('Please enter a test message');
            return;
        }

        setLoading(true);
        setSuggestions([]);

        try {
            const data = await apiRequest('/suggestion', {
                method: 'POST',
                body: JSON.stringify({
                    message: testMessage,
                    user_line_id: testUser || 'test_user',
                    limit: 5
                })
            });

            if (data.success) {
                setSuggestions(data.suggestions || []);

                // Log suggestion details for monitoring
                setTrainingLog(prev => prev + `\n💡 Suggestion Analysis:\n`);
                setTrainingLog(prev => prev + `   Message: "${testMessage}"\n`);
                setTrainingLog(prev => prev + `   Total suggestions: ${data.count}\n`);
                setTrainingLog(prev => prev + `   ML suggestions: ${data.ml_suggestions_count || 0}\n`);
                setTrainingLog(prev => prev + `   Processing time: ${data.processing_time || 'N/A'}ms\n`);
            } else {
                alert(`Error: ${data.error}`);
            }
        } catch (error) {
            alert(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Toggle automated training
    const toggleAutomatedTraining = async (enabled) => {
        try {
            const data = await apiRequest('/suggestion', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'toggle_automation',
                    enabled: enabled
                })
            });

            if (data.success) {
                alert(`✅ Automated training ${enabled ? 'enabled' : 'disabled'}`);
                refreshStatus();
            } else {
                alert('❌ Failed to toggle automated training');
            }
        } catch (error) {
            alert('❌ Error: ' + error.message);
        }
    };

    // Dismiss alert
    const dismissAlert = (alertId) => {
        setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    };

    // Initialize and setup real-time monitoring
    useEffect(() => {
        refreshStatus();

        // Setup real-time monitoring intervals
        const statusInterval = setInterval(refreshStatus, 30000); // Every 30 seconds
        const metricsInterval = setInterval(() => {
            setPerformanceMetrics(prev => {
                const newPoint = {
                    time: new Date().toLocaleTimeString(),
                    accuracy: (stats.avg_confidence || 0) * 100,
                    usage: stats.usage_rate || 0,
                    suggestions: stats.suggestions_last_7_days || 0
                };
                return [...prev.slice(-19), newPoint]; // Keep last 20 points
            });
        }, 60000); // Every minute

        return () => {
            clearInterval(statusInterval);
            clearInterval(metricsInterval);
        };
    }, [refreshStatus]);

    // Tab navigation
    const tabs = [
        { id: 'overview', label: '📊 Overview', icon: '📊' },
        { id: 'monitoring', label: '📈 Real-time Monitoring', icon: '📈' },
        { id: 'training', label: '🏋️ Training', icon: '🏋️' },
        { id: 'testing', label: '🧪 Testing', icon: '🧪' },
        { id: 'automation', label: '🤖 Automation', icon: '🤖' },
        { id: 'settings', label: '⚙️ Settings', icon: '⚙️' }
    ];

    return (
        <MainLayout
            pageContent={
                <div className='flex flex-col px-6 py-8 max-w-7xl mx-auto'>
                    <div className="bg-white rounded-lg shadow-lg">
                        {/* Header */}
                        <div className="p-8 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h1 className="text-3xl font-bold text-gray-800">
                                    🤖 ML System Control Center
                                </h1>
                                <div className="flex items-center space-x-4">
                                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${status?.success
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                        {status?.success ? '🟢 Online' : '🔴 Offline'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Last update: {lastUpdate.toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>

                            {/* Alerts Banner */}
                            {alerts.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {alerts.slice(-3).map(alert => (
                                        <div key={alert.id} className={`p-3 rounded-md flex justify-between items-center ${alert.type === 'error' ? 'bg-red-50 text-red-800' :
                                                alert.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                                                    'bg-blue-50 text-blue-800'
                                            }`}>
                                            <span>{alert.message}</span>
                                            <button
                                                onClick={() => dismissAlert(alert.id)}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Tab Navigation */}
                        <div className="border-b border-gray-200">
                            <nav className="flex">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {/* Tab Content */}
                        <div className="p-8">
                            {/* Overview Tab */}
                            {activeTab === 'overview' && (
                                <div className="space-y-8">
                                    {/* System Status Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-blue-100">Suggestions (7d)</p>
                                                    <p className="text-3xl font-bold">{stats.suggestions_last_7_days || 0}</p>
                                                </div>
                                                <div className="text-4xl opacity-80">💡</div>
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-green-100">ML Usage Rate</p>
                                                    <p className="text-3xl font-bold">{stats.usage_rate || 0}%</p>
                                                </div>
                                                <div className="text-4xl opacity-80">🎯</div>
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-purple-100">Avg Confidence</p>
                                                    <p className="text-3xl font-bold">{Math.round((stats.avg_confidence || 0) * 100)}%</p>
                                                </div>
                                                <div className="text-4xl opacity-80">⚡</div>
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-orange-100">System Status</p>
                                                    <p className="text-xl font-bold">{stats.ml_enabled ? 'Active' : 'Inactive'}</p>
                                                </div>
                                                <div className="text-4xl opacity-80">🤖</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Health Status */}
                                    {healthData && (
                                        <div className="bg-gray-50 p-6 rounded-lg">
                                            <h3 className="text-lg font-semibold mb-4">🩺 System Health</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="bg-white p-4 rounded-lg">
                                                    <h4 className="font-medium text-gray-700">ML Service</h4>
                                                    <p className={`text-sm ${healthData.ml_service?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                                                        {healthData.ml_service?.enabled ? '✅ Online' : '❌ Offline'}
                                                    </p>
                                                </div>
                                                <div className="bg-white p-4 rounded-lg">
                                                    <h4 className="font-medium text-gray-700">Database</h4>
                                                    <p className={`text-sm ${healthData.database?.connected ? 'text-green-600' : 'text-red-600'}`}>
                                                        {healthData.database?.connected ? '✅ Connected' : '❌ Disconnected'}
                                                    </p>
                                                </div>
                                                <div className="bg-white p-4 rounded-lg">
                                                    <h4 className="font-medium text-gray-700">Cache</h4>
                                                    <p className="text-sm text-green-600">✅ Enabled</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Real-time Monitoring Tab */}
                            {activeTab === 'monitoring' && (
                                <div className="space-y-8">
                                    <h2 className="text-2xl font-semibold text-gray-700">📈 Real-time Performance</h2>

                                    {performanceMetrics.length > 0 && (
                                        <div className="bg-white p-6 rounded-lg border">
                                            <h3 className="text-lg font-semibold mb-4">Performance Trends</h3>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={performanceMetrics}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="time" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Line type="monotone" dataKey="accuracy" stroke="#3B82F6" name="Accuracy %" />
                                                    <Line type="monotone" dataKey="usage" stroke="#10B981" name="Usage Rate %" />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}

                                    {/* Live Metrics */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="bg-white p-6 rounded-lg border">
                                            <h3 className="text-lg font-semibold mb-4">⚡ Live Metrics</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <span>Current Accuracy:</span>
                                                    <span className="font-bold text-blue-600">{Math.round((stats.avg_confidence || 0) * 100)}%</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span>Usage Rate:</span>
                                                    <span className="font-bold text-green-600">{stats.usage_rate || 0}%</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span>Response Time:</span>
                                                    <span className="font-bold text-purple-600">{stats.avg_response_time || 'N/A'}ms</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white p-6 rounded-lg border">
                                            <h3 className="text-lg font-semibold mb-4">📊 Activity Summary</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <span>Today's Suggestions:</span>
                                                    <span className="font-bold">{stats.suggestions_today || 0}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span>This Week:</span>
                                                    <span className="font-bold">{stats.suggestions_last_7_days || 0}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span>Last Training:</span>
                                                    <span className="font-bold">{stats.last_training || 'Never'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Training Tab */}
                            {activeTab === 'training' && (
                                <div className="space-y-8">
                                    <h2 className="text-2xl font-semibold text-gray-700">🏋️ Model Training</h2>

                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Training Data Size:
                                            </label>
                                            <input
                                                type="number"
                                                value={trainingLimit}
                                                onChange={(e) => setTrainingLimit(e.target.value)}
                                                min="50"
                                                max="50000"
                                                className="w-full p-3 border border-gray-300 rounded-md"
                                                placeholder="Enter number of conversations"
                                            />
                                            <p className="text-sm text-gray-500 mt-2">
                                                Recommended: 1000-5000 for development, 10000+ for production
                                            </p>
                                        </div>

                                        <div className="flex flex-wrap gap-3 mb-6">
                                            <button
                                                onClick={startTraining}
                                                disabled={loading}
                                                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                            >
                                                🚀 Start Training
                                            </button>
                                            <button
                                                onClick={testModel}
                                                disabled={loading}
                                                className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                            >
                                                🧪 Test Model
                                            </button>
                                            <button
                                                onClick={() => alert('Progressive training UI would go here')}
                                                className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                                            >
                                                📈 Progressive Training
                                            </button>
                                        </div>

                                        {/* Training History */}
                                        {trainingHistory.length > 0 && (
                                            <div className="mt-6">
                                                <h3 className="text-lg font-semibold mb-4">📊 Training History</h3>
                                                <div className="bg-white rounded-lg overflow-hidden">
                                                    <table className="w-full">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left">Date</th>
                                                                <th className="px-4 py-3 text-left">Status</th>
                                                                <th className="px-4 py-3 text-left">Accuracy</th>
                                                                <th className="px-4 py-3 text-left">Duration</th>
                                                                <th className="px-4 py-3 text-left">Samples</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {trainingHistory.map((training, index) => (
                                                                <tr key={index} className="border-t">
                                                                    <td className="px-4 py-3">{training.date}</td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`px-2 py-1 rounded-full text-xs ${training.status === 'completed'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                            }`}>
                                                                            {training.status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3">{Math.round(training.accuracy * 100)}%</td>
                                                                    <td className="px-4 py-3">{training.duration}s</td>
                                                                    <td className="px-4 py-3">{training.samples?.toLocaleString()}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Training Log */}
                                    {showLog && (
                                        <div className="bg-black text-green-400 p-4 rounded-lg h-64 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
                                            {trainingLog}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Testing Tab */}
                            {activeTab === 'testing' && (
                                <div className="space-y-8">
                                    <h2 className="text-2xl font-semibold text-gray-700">🧪 Model Testing</h2>

                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Test Message (Thai):
                                                </label>
                                                <textarea
                                                    value={testMessage}
                                                    onChange={(e) => setTestMessage(e.target.value)}
                                                    className="w-full p-3 border border-gray-300 rounded-md h-24"
                                                    placeholder="Enter a Thai message to test..."
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    User ID (optional):
                                                </label>
                                                <input
                                                    type="text"
                                                    value={testUser}
                                                    onChange={(e) => setTestUser(e.target.value)}
                                                    className="w-full p-3 border border-gray-300 rounded-md"
                                                    placeholder="test_user_123"
                                                />

                                                <button
                                                    onClick={getSuggestions}
                                                    disabled={loading}
                                                    className="w-full mt-4 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                                >
                                                    💡 Get Suggestions
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quick Test Buttons */}
                                        <div className="mt-6">
                                            <h3 className="text-lg font-semibold mb-3">⚡ Quick Tests</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                {[
                                                    'สวัสดีครับ มีปัญหาเรื่อง VST ไม่ทำงาน',
                                                    'Cubase เสียงไม่ออกครับ ช่วยแก้ไขให้หน่อย',
                                                    'ขอบคุณครับ แก้ไขปัญหาได้แล้ว เยี่ยมมาก'
                                                ].map((msg, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => setTestMessage(msg)}
                                                        className="p-3 text-left bg-white border border-gray-200 rounded-md hover:bg-gray-50 text-sm"
                                                    >
                                                        {msg.substring(0, 40)}...
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Test Results */}
                                    {suggestions.length > 0 && (
                                        <div className="bg-white p-6 rounded-lg border">
                                            <h3 className="text-lg font-semibold mb-4">💡 Test Results</h3>
                                            <div className="space-y-4">
                                                {suggestions.map((suggestion, index) => {
                                                    const confidence = Math.round(suggestion.confidence * 100);
                                                    return (
                                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <span className="font-semibold text-gray-700">#{index + 1}</span>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className={`px-2 py-1 rounded-full text-xs ${suggestion.source === 'ml'
                                                                            ? 'bg-blue-100 text-blue-800'
                                                                            : 'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                        {suggestion.source || 'Database'}
                                                                    </span>
                                                                    <span className={`px-2 py-1 rounded-full text-xs ${confidence >= 80 ? 'bg-green-100 text-green-800' :
                                                                            confidence >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                                                                'bg-red-100 text-red-800'
                                                                        }`}>
                                                                        {confidence}%
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <p className="text-gray-800 mb-3">{suggestion.response}</p>

                                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className={`h-2 rounded-full transition-all duration-300 ${confidence >= 80 ? 'bg-green-500' :
                                                                            confidence >= 60 ? 'bg-yellow-500' :
                                                                                'bg-red-500'
                                                                        }`}
                                                                    style={{ width: `${confidence}%` }}
                                                                ></div>
                                                            </div>

                                                            <div className="mt-2 text-xs text-gray-500">
                                                                Match Type: {suggestion.match_type || 'N/A'} |
                                                                Response Time: {suggestion.response_time || 'N/A'}ms
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Automation Tab */}
                            {activeTab === 'automation' && (
                                <div className="space-y-8">
                                    <h2 className="text-2xl font-semibold text-gray-700">🤖 Automated Training</h2>

                                    {/* Automation Status */}
                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <h3 className="text-lg font-semibold mb-4">📋 Automation Status</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            <div className="bg-white p-4 rounded-lg">
                                                <h4 className="font-medium text-gray-700 mb-2">Daily Checks</h4>
                                                <p className={`text-sm ${automatedTrainingConfig.daily_enabled ? 'text-green-600' : 'text-red-600'}`}>
                                                    {automatedTrainingConfig.daily_enabled ? '✅ Enabled' : '❌ Disabled'}
                                                </p>
                                                <p className="text-xs text-gray-500">Next: {automatedTrainingConfig.next_daily || 'N/A'}</p>
                                            </div>

                                            <div className="bg-white p-4 rounded-lg">
                                                <h4 className="font-medium text-gray-700 mb-2">Weekly Training</h4>
                                                <p className={`text-sm ${automatedTrainingConfig.weekly_enabled ? 'text-green-600' : 'text-red-600'}`}>
                                                    {automatedTrainingConfig.weekly_enabled ? '✅ Enabled' : '❌ Disabled'}
                                                </p>
                                                <p className="text-xs text-gray-500">Next: {automatedTrainingConfig.next_weekly || 'N/A'}</p>
                                            </div>

                                            <div className="bg-white p-4 rounded-lg">
                                                <h4 className="font-medium text-gray-700 mb-2">Health Monitoring</h4>
                                                <p className={`text-sm ${automatedTrainingConfig.health_enabled ? 'text-green-600' : 'text-red-600'}`}>
                                                    {automatedTrainingConfig.health_enabled ? '✅ Active' : '❌ Inactive'}
                                                </p>
                                                <p className="text-xs text-gray-500">Every: {automatedTrainingConfig.health_interval || 'N/A'} hours</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Automation Controls */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">🎛️ Automation Controls</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium">Enable Automated Training</h4>
                                                    <p className="text-sm text-gray-500">Automatically train model based on data analysis</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={automatedTrainingConfig.enabled || false}
                                                        onChange={(e) => toggleAutomatedTraining(e.target.checked)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium">Email Notifications</h4>
                                                    <p className="text-sm text-gray-500">Get notified about training events</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={automatedTrainingConfig.notifications || false}
                                                        onChange={(e) => {
                                                            setAutomatedTrainingConfig(prev => ({
                                                                ...prev,
                                                                notifications: e.target.checked
                                                            }));
                                                        }}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Training Triggers */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">🎯 Training Triggers</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <h4 className="font-medium mb-2">Data Thresholds</h4>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm text-gray-600">Min new conversations:</label>
                                                        <input
                                                            type="number"
                                                            defaultValue={automatedTrainingConfig.min_conversations || 100}
                                                            className="w-full p-2 border border-gray-300 rounded"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm text-gray-600">Min days between training:</label>
                                                        <input
                                                            type="number"
                                                            defaultValue={automatedTrainingConfig.min_days || 7}
                                                            className="w-full p-2 border border-gray-300 rounded"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-medium mb-2">Quality Thresholds</h4>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm text-gray-600">Min accuracy (%):</label>
                                                        <input
                                                            type="number"
                                                            defaultValue={Math.round((automatedTrainingConfig.quality_threshold || 0.7) * 100)}
                                                            min="0"
                                                            max="100"
                                                            className="w-full p-2 border border-gray-300 rounded"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm text-gray-600">Min usage rate (%):</label>
                                                        <input
                                                            type="number"
                                                            defaultValue={automatedTrainingConfig.usage_threshold || 50}
                                                            min="0"
                                                            max="100"
                                                            className="w-full p-2 border border-gray-300 rounded"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <button className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                            💾 Save Configuration
                                        </button>
                                    </div>

                                    {/* Recent Automation Activity */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">📊 Recent Activity</h3>
                                        <div className="space-y-3">
                                            {[
                                                { time: '2 hours ago', action: 'Health check completed', status: 'success' },
                                                { time: '1 day ago', action: 'Daily analysis - no training needed', status: 'info' },
                                                { time: '3 days ago', action: 'Automated training completed', status: 'success' },
                                                { time: '1 week ago', action: 'Weekly training initiated', status: 'success' }
                                            ].map((activity, index) => (
                                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                                    <div>
                                                        <p className="font-medium">{activity.action}</p>
                                                        <p className="text-sm text-gray-500">{activity.time}</p>
                                                    </div>
                                                    <span className={`px-2 py-1 rounded-full text-xs ${activity.status === 'success' ? 'bg-green-100 text-green-800' :
                                                            activity.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {activity.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Settings Tab */}
                            {activeTab === 'settings' && (
                                <div className="space-y-8">
                                    <h2 className="text-2xl font-semibold text-gray-700">⚙️ System Settings</h2>

                                    {/* ML Engine Settings */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">🤖 ML Engine Configuration</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium">Enable ML Service</h4>
                                                    <p className="text-sm text-gray-500">Turn ML-powered suggestions on/off</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={stats.ml_enabled || false}
                                                        onChange={(e) => setStats(prev => ({ ...prev, ml_enabled: e.target.checked }))}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium">Hybrid Mode</h4>
                                                    <p className="text-sm text-gray-500">Mix ML suggestions with database matches</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={stats.hybrid_mode || false}
                                                        onChange={(e) => setStats(prev => ({ ...prev, hybrid_mode: e.target.checked }))}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    ML Service URL:
                                                </label>
                                                <input
                                                    type="text"
                                                    defaultValue="http://localhost:3058"
                                                    className="w-full p-3 border border-gray-300 rounded-md"
                                                    placeholder="http://localhost:3058"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Cache Duration (seconds):
                                                </label>
                                                <input
                                                    type="number"
                                                    defaultValue={300}
                                                    className="w-full p-3 border border-gray-300 rounded-md"
                                                    min="0"
                                                    max="3600"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Performance Settings */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">⚡ Performance Settings</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Default Suggestion Limit:
                                                </label>
                                                <input
                                                    type="number"
                                                    defaultValue={5}
                                                    className="w-full p-3 border border-gray-300 rounded-md"
                                                    min="1"
                                                    max="20"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Minimum Relevance Score:
                                                </label>
                                                <input
                                                    type="number"
                                                    defaultValue={0.5}
                                                    step="0.1"
                                                    className="w-full p-3 border border-gray-300 rounded-md"
                                                    min="0"
                                                    max="1"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* System Actions */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h3 className="text-lg font-semibold mb-4">🛠️ System Actions</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <button
                                                onClick={() => refreshStatus()}
                                                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                🔄 Refresh Status
                                            </button>

                                            <button
                                                onClick={() => alert('Cache cleared!')}
                                                className="px-6 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                                            >
                                                🗑️ Clear Cache
                                            </button>

                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Are you sure you want to reset the ML model? This action cannot be undone.')) {
                                                        alert('🚧 Reset functionality not implemented yet.');
                                                    }
                                                }}
                                                className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700"
                                            >
                                                🔄 Reset Model
                                            </button>
                                        </div>
                                    </div>

                                    {/* Save Settings */}
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => {
                                                // Save all settings
                                                alert('✅ Settings saved successfully!');
                                            }}
                                            className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                                        >
                                            💾 Save All Settings
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            }
        />
    );
}